import axios from "axios";
import moment from "moment";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Divider, Typography } from "@mui/material";
import { RichiesteAccessoHeader } from "../../../const/DatatableData";
import { GetAllRichiestaCredenziali } from "../../../const/urlAction";
import CustomStyledDataGrid from "../../UI/DataGrid/StyledDataGrid";
import MobileViewContainer from "../../UI/mobileView/mobileViewContainer";
import MobileViewList from "../../UI/mobileView/list/MobileViewList";
import { rowStructuresEnum } from "../../UI/mobileView/enum/rowStructuresEnum";
import { CDCard, CDCardContent, CDCardHeader } from "../../../const/CDCardStyles";
import { TitleDatable } from "../../TitleDatable/TitleDataTable";
import TableToolbar from "../../UI/TableToolbar/TableToolbar";
import useViewportAdaptedHeight from "../../../Hooks/Adaptive/useViewportAdaptedHeight";
import handlePaginationAndRowCleanupMobileView from "../../../functions/mobileView/handlePaginationAndRowCleanupMobileView";
import { useSelector } from "react-redux";
import ButtonBoxRichiesteCredenziali from "../RichiestaCredenzialiAccesso/RichiesteAccessoComponent/ButtonBoxRichiesteCredenziali";

const rowPage = 10;
const urlBase = process.env.REACT_APP_URL;

export default function RichiesteAccessoComponent() {
  const { t } = useTranslation();
  const viewState = useSelector((state) => state.viewState);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [elements, setElements] = useState([]);
  const [loadingRow, setLoadingRow] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rowsCount, setRowsCount] = useState(0);

  // Paginazione e ricerca
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPage);
  const [searchFilter, setSearchFilter] = useState("");
  const [searchStatus, setSearchStatus] = useState(false);

  const lastSearchFilter = useRef();
  const lastPageIndex = useRef();
  const CDCardContentRef = useRef(null);

  const adaptedHeight = useViewportAdaptedHeight(
    ".adaptHeight",
    19,
    CDCardContentRef,
    !isLoading,
    [viewState, isMobile]
  );

  const handleSearchFilter = (searchText) => {
    setSearchFilter(searchText);
    setSearchStatus((prev) => !prev);
  };

  const handleResizeWindow = () => {
    setIsMobile(window.innerWidth < 600);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResizeWindow);
    return () => window.removeEventListener("resize", handleResizeWindow);
  });

  // Reset su cambio viewState (desktop ↔ mobile view)
  useEffect(() => {
    if (page !== 0 || pageSize !== rowPage) setElements([]);
    setPage(0);
    setPageSize(rowPage);
  }, [viewState]);

  useEffect(() => {
    let isMounted = true;

    const requestBody = {
      pageIndex: page,
      pageSize: pageSize,
      applyPaginationAndSort: true,
      searchFilter: searchFilter,
      sortColumnName: RichiesteAccessoHeader.initialFieldName,
      sortDirection: 1,
    };

    const cleanRow = handlePaginationAndRowCleanupMobileView(
      lastSearchFilter.current,
      lastPageIndex.current,
      searchFilter,
      page,
      pageSize,
      requestBody
    );

    (async () => {
      const { action, method } = GetAllRichiestaCredenziali;
      const url = urlBase + action;
      setIsLoading(true);
      try {
        const response = await axios({ method, url, data: requestBody });
        if (response.data.ok && isMounted) {
          setRowsCount(response.data.result.totalFiltered);
          const elementsFormatted = response.data.result.result.map((x) => ({
            ...x,
            dateRequest: moment(x.dateRequest).format("DD/MM/YYYY HH:mm"),
          }));

          cleanRow || (!isMobile && !viewState)
            ? setElements(elementsFormatted)
            : setElements((old) => [...old, ...elementsFormatted]);

          lastSearchFilter.current = requestBody.searchFilter;
          lastPageIndex.current = requestBody.pageIndex;
        }
      } catch (error) {
        console.error(error);
      }
      if (isMounted) setIsLoading(false);
    })();

    return () => { isMounted = false; };
  }, [page, pageSize, searchStatus, loadingRow, viewState]);

  // ── MOBILE ──────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Grid container variant="No-Padding">
        <Grid item component={CDCard} xs={12} sm={12}>
          <CDCardHeader
            className="CDCardHeader-Padding adaptHeight"
            title={<TitleDatable nameDataTable={t("request_credentials")} />}
          />
          <Divider />
          <CDCardContent
            className="CDCardContent-Mobile small-padding-mobilerow"
            style={{ height: adaptedHeight }}
            ref={CDCardContentRef}
          >
            <Grid container item>
              <MobileViewContainer>
                <TableToolbar
                  enableSearch={true}
                  enableAdd={false}
                  enableExport={false}
                  isSalaRiunione={false}
                  initialSearchText={searchFilter}
                  startSearch={handleSearchFilter}
                />
              </MobileViewContainer>
              <MobileViewList
                rows={elements}
                BoxButton={ButtonBoxRichiesteCredenziali}
                typeStructure={rowStructuresEnum.RichiesteAccessoRowStructures}
                setPage={setPage}
                areMoreRowsAvailable={(page + 1) * pageSize < rowsCount}
              />
            </Grid>
          </CDCardContent>
        </Grid>
      </Grid>
    );
  }

  // ── DESKTOP viewState (tablet-style list) ───────────────────────────────────
  if (viewState) {
    return (
      <Grid container variant="No-Padding">
        <Grid item component={CDCard} xs={12} sm={12}>
          <CDCardHeader
            className="CDCardHeader-Padding adaptHeight"
            title={<TitleDatable nameDataTable={t("request_credentials")} />}
          />
          <Divider />
          <div className="adaptHeight" style={{ backgroundColor: "white" }}>
            <div style={{ margin: "20px" }}>
              <TableToolbar
                enableSearch={true}
                enableAdd={false}
                enableExport={false}
                isSalaRiunione={false}
                initialSearchText={searchFilter}
                startSearch={handleSearchFilter}
              />
            </div>
          </div>
          <CDCardContent
            className="CDCardContent-Mobile"
            style={{ height: adaptedHeight }}
            ref={CDCardContentRef}
          >
            <Grid container item>
              <MobileViewList
                rows={elements}
                typeStructure={rowStructuresEnum.RichiesteAccessoRowStructures}
                setPage={setPage}
                areMoreRowsAvailable={(page + 1) * pageSize < rowsCount}
              />
            </Grid>
          </CDCardContent>
        </Grid>
      </Grid>
    );
  }

  // ── DESKTOP standard (DataGrid) ─────────────────────────────────────────────
  return (
    <CustomStyledDataGrid
      headers={RichiesteAccessoHeader}
      rows={elements}
      rowHeight={80}
      idRowsfield={"idRichiesta"}
      nameDataGrid={t("request_credentials")}
      reloadRow={setLoadingRow}
      setSateSpinner={isLoading}
      setRequestBody={(body) => {
        // CustomStyledDataGrid gestisce internamente paginazione/sort:
        // propaga il requestBody aggiornato triggherando il fetch via loadingRow
        setLoadingRow(body);
      }}
      rowsCount={rowsCount}
    />
  );
}